import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faCoins, faGamepad, faPhone, faShieldHalved } from '@fortawesome/free-solid-svg-icons';

const pageContent = {
  consoles: {
    stage: 'Player Select',
    eyebrow: 'Consoles at Game On',
    title: 'Your next console starts here',
    intro: 'Shop current-generation, previous-generation and retro consoles with straightforward local advice and stock checked before sale.',
    heroImage: '/images/2026/categories/consoles/hero.webp',
    heroAlt: 'Console stock available from Game On Cosham',
    primary: 'Ask what is in stock',
    highlights: [
      ['PlayStation', 'PS5, PS4, controllers, storage and pre-owned favourites.'],
      ['Xbox', 'Series consoles, Xbox One systems, pads and accessories.'],
      ['Nintendo', 'Switch systems, handhelds, family games and retro hardware.']
    ],
    stepsTitle: 'Choose your next system',
    steps: ['Tell us what and how you play', 'Compare new and pre-owned options', 'Trade old hardware against the upgrade'],
    gallery: ['/images/2026/categories/consoles/detail-1.png', '/images/2026/categories/consoles/detail-2.png', '/images/2026/categories/consoles/detail-3.png']
  },
  games: {
    stage: 'Continue?',
    eyebrow: 'Games at Game On',
    title: 'Build a library worth replaying',
    intro: 'Discover new releases, pre-owned bargains and older favourites across PlayStation, Xbox, Nintendo and retro formats.',
    heroImage: '/images/2026/categories/games/hero.webp',
    heroAlt: 'Pre-owned games available at Game On Cosham',
    primary: 'Find your next game',
    highlights: [
      ['New releases', 'The latest major releases and editions for current systems.'],
      ['Pre-owned', 'More games for the money, checked and ready for another player.'],
      ['Retro finds', 'Cartridges, discs and classics from generations past.']
    ],
    stepsTitle: 'Turn finished games into new ones',
    steps: ['Bring games into the Cosham shop', 'We check condition and current demand', 'Take cash or put the value towards something else'],
    gallery: ['/images/2026/categories/games/detail-1.png', '/images/2026/categories/games/detail-2.png', '/images/2026/categories/games/detail-3.png']
  },
  tech: {
    stage: 'System Upgrade',
    eyebrow: 'Tech at Game On',
    title: 'Phones, laptops and gear that works',
    intro: 'Buy and trade practical technology locally, from phones and laptops to headsets, controllers, storage and everyday accessories.',
    heroImage: '/images/2026/categories/tech/hero.webp',
    heroAlt: 'Phones, computers and gaming technology at Game On Cosham',
    primary: 'Ask about tech',
    highlights: [
      ['Phones and tablets', 'Quality mobile devices with simple local trade-in options.'],
      ['Laptops', 'Everyday machines, gaming-capable systems and upgrade advice.'],
      ['Accessories', 'Headsets, controllers, chargers, storage, cables and more.']
    ],
    stepsTitle: 'Upgrade without the guesswork',
    steps: ['Tell us what the device needs to do', 'Compare practical options at your budget', 'Trade unused technology to reduce the cost'],
    gallery: ['/images/2026/categories/tech/detail-1.png', '/images/2026/categories/tech/detail-2.png', '/images/2026/categories/tech/detail-3.png']
  },
  trade: {
    stage: 'Cash Bonus',
    eyebrow: 'Trade in at Game On',
    title: 'Turn old gear into your next upgrade',
    intro: 'Thinking of trading in consoles, games, phones, laptops, PCs or accessories? Call our Cosham store first to discuss what you have and what to bring.',
    heroImage: '/images/2026/categories/trade-in/hero.webp',
    heroAlt: 'Trade games and technology for cash at Game On Cosham',
    primary: 'Call 02392 383 313',
    highlights: [
      ['Cash or credit', 'Choose instant value or put it towards something new to you.'],
      ['Fast checks', 'We inspect condition, test the item and explain the valuation.'],
      ['More than games', 'Trade phones, laptops, tablets, consoles, PCs and accessories.']
    ],
    stepsTitle: 'Three steps to a trade-in',
    steps: ['Call 02392 383 313 to discuss the items you want to trade', 'Bring your items, cables and identification into the store', 'We test the equipment and offer cash or store credit'],
    gallery: ['/images/2026/categories/trade-in/detail-1.png', '/images/2026/categories/trade-in/detail-2.png', '/images/2026/categories/trade-in/detail-3.png']
  }
};

const CategoryPage = ({ type }) => {
  const page = pageContent[type];

  return (
    <div className={`content-page retail-page retail-${type}`}>
      <section className="retail-hero">
        <div className="retail-hero-copy">
          <p className="pixel-kicker">{page.stage}</p>
          <p className="eyebrow">{page.eyebrow}</p>
          <h1>{page.title}</h1>
          <p>{page.intro}</p>
          <div className="hero-actions">
            {type === 'trade' ? (
              <>
                <a className="pixel-button" href="tel:02392383313">{page.primary} <FontAwesomeIcon icon={faPhone} /></a>
                <Link className="btn-ghost" to="/contact">Shop details</Link>
              </>
            ) : (
              <Link className="pixel-button" to="/contact">{page.primary} <FontAwesomeIcon icon={faArrowRight} /></Link>
            )}
            {type !== 'trade' && <Link className="btn-ghost" to="/trade-in">Trade something in</Link>}
          </div>
        </div>
        <div className="retail-hero-image">
          <img src={page.heroImage} alt={page.heroAlt} fetchPriority="high" />
          <span>{page.stage}</span>
        </div>
      </section>

      {type === 'trade' && (
        <section className="trade-phone-prompt" aria-labelledby="trade-phone-title">
          <FontAwesomeIcon icon={faPhone} />
          <div>
            <p className="pixel-kicker">Call before you visit</p>
            <h2 id="trade-phone-title">Discuss your trade-in with the store</h2>
            <p>Tell us what you have, its condition and which cables or accessories are included. We will explain what to bring for an in-store valuation.</p>
          </div>
          <a href="tel:02392383313">
            <span>Call Game On Cosham</span>
            <strong>02392 383 313</strong>
          </a>
        </section>
      )}

      <section className="retail-highlights" aria-label={`${page.eyebrow} categories`}>
        {page.highlights.map(([title, copy], index) => (
          <article key={title}>
            <span>0{index + 1}</span>
            <FontAwesomeIcon icon={type === 'trade' ? faCoins : faGamepad} />
            <h2>{title}</h2>
            <p>{copy}</p>
          </article>
        ))}
      </section>

      <section className="retail-process">
        <div>
          <p className="pixel-kicker">Mission Brief</p>
          <h2>{page.stepsTitle}</h2>
          <p>Friendly advice from a real local shop, with no anonymous forms or confusing postage process.</p>
        </div>
        <ol>
          {page.steps.map((step, index) => (
            <li key={step}><span>{index + 1}</span><p>{step}</p><FontAwesomeIcon icon={faCheck} /></li>
          ))}
        </ol>
      </section>

      <section className="retail-gallery" aria-label={`${page.eyebrow} gallery`}>
        {page.gallery.map((image, index) => (
          <img
            src={image}
            alt={`${page.highlights[index][0]} at Game On Cosham`}
            key={image}
            className={index === 0 ? 'is-featured' : ''}
            loading="lazy"
            decoding="async"
          />
        ))}
      </section>

      <section className="visit-callout">
        <div>
          <p className="pixel-kicker">Local co-op</p>
          <h2>Visit Game On in Cosham</h2>
          <p><FontAwesomeIcon icon={faShieldHalved} /> Checked stock, straightforward prices and a team that knows the gear.</p>
        </div>
        <Link className="pixel-button" to="/contact">Get directions <FontAwesomeIcon icon={faArrowRight} /></Link>
      </section>
    </div>
  );
};

export default CategoryPage;
